<template>
  <div class="hb-assist-root" :class="{ open: isOpen }">
    <button
      type="button"
      class="hb-assist-launcher"
      :class="{ pulse: !hasOpenedOnce }"
      @click="toggle"
      :aria-label="isOpen ? 'Close help assistant' : 'Open help assistant'"
    >
      <span v-if="!isOpen" class="hb-assist-launcher-inner">
        <i class="bx bx-message-rounded-dots"></i>
        <span class="hb-assist-launcher-text">Need help?</span>
      </span>
      <i v-else class="bx bx-x"></i>
    </button>

    <transition name="hb-assist-pop">
      <div v-if="isOpen" class="hb-assist-panel" role="dialog" aria-label="Login help assistant">
        <header class="hb-assist-header">
          <div class="hb-assist-avatar">
            <i class="bx bx-bot"></i>
          </div>
          <div class="hb-assist-title-wrap">
            <h4>HRIS Assistant</h4>
            <p>
              <span class="hb-assist-status-dot"></span>
              Online · Average reply &lt; 1s
            </p>
          </div>
          <button
            type="button"
            class="hb-assist-icon-btn"
            @click="resetConversation"
            title="Start over"
            aria-label="Start over"
          >
            <i class="bx bx-refresh"></i>
          </button>
          <button
            type="button"
            class="hb-assist-icon-btn"
            @click="toggle"
            title="Close"
            aria-label="Close assistant"
          >
            <i class="bx bx-x"></i>
          </button>
        </header>

        <div ref="scrollEl" class="hb-assist-body">
          <div
            v-for="(msg, idx) in messages"
            :key="idx"
            class="hb-assist-msg"
            :class="msg.from === 'bot' ? 'bot' : 'user'"
          >
            <div v-if="msg.from === 'bot'" class="hb-assist-bubble">
              <div v-if="msg.html" v-html="msg.html"></div>
              <div v-else>{{ msg.text }}</div>

              <ul v-if="msg.steps && msg.steps.length" class="hb-assist-steps">
                <li v-for="(step, sIdx) in msg.steps" :key="sIdx">
                  <span class="hb-assist-step-num">{{ sIdx + 1 }}</span>
                  <span v-html="step"></span>
                </li>
              </ul>

              <div v-if="msg.actions && msg.actions.length" class="hb-assist-actions">
                <button
                  v-for="(act, aIdx) in msg.actions"
                  :key="aIdx"
                  type="button"
                  class="hb-assist-action"
                  :class="act.variant || ''"
                  :disabled="act.disabled"
                  @click="runAction(act)"
                >
                  <i v-if="act.icon" :class="act.icon"></i>
                  {{ act.label }}
                </button>
              </div>
            </div>
            <div v-else class="hb-assist-bubble user-bubble">{{ msg.text }}</div>
          </div>

          <div v-if="typing" class="hb-assist-msg bot">
            <div class="hb-assist-bubble typing">
              <span class="dot"></span>
              <span class="dot"></span>
              <span class="dot"></span>
            </div>
          </div>
        </div>

        <div v-if="quickReplies.length" class="hb-assist-quick">
          <button
            v-for="(q, qIdx) in quickReplies"
            :key="qIdx"
            type="button"
            class="hb-assist-chip"
            @click="handleQuickReply(q)"
          >
            {{ q.label }}
          </button>
        </div>

        <form class="hb-assist-input" @submit.prevent="sendUserMessage">
          <input
            v-model="userInput"
            type="text"
            placeholder="Type your question…"
            aria-label="Type your question"
          />
          <button type="submit" :disabled="!userInput.trim()">
            <i class="bx bx-send"></i>
          </button>
        </form>

        <footer class="hb-assist-footer">
          <i class="bx bx-shield-quarter"></i>
          Replies are automated. Never share your password here.
        </footer>
      </div>
    </transition>
  </div>
</template>

<script>
/* eslint-disable */
import { authUrl, execute2 } from "@/api";

const EMAIL_RE = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
const ID_RE = /^\d{6,12}$/;

export default {
  name: "LoginAssistant",
  data() {
    return {
      isOpen: false,
      hasOpenedOnce: false,
      typing: false,
      messages: [],
      quickReplies: [],
      userInput: "",
      awaitingIdentifierFor: null,
      lastIdentifier: "",
      sendingReset: false,
    };
  },
  mounted() {
    try {
      const saved = sessionStorage.getItem("hb_assist_opened_once");
      if (saved === "1") this.hasOpenedOnce = true;
    } catch (_) {}
    this.bootstrapConversation();
  },
  methods: {
    toggle() {
      this.isOpen = !this.isOpen;
      if (this.isOpen) {
        this.hasOpenedOnce = true;
        try {
          sessionStorage.setItem("hb_assist_opened_once", "1");
        } catch (_) {}
        this.$nextTick(this.scrollToBottom);
      }
    },
    resetConversation() {
      this.messages = [];
      this.quickReplies = [];
      this.userInput = "";
      this.awaitingIdentifierFor = null;
      this.bootstrapConversation();
    },
    bootstrapConversation() {
      this.pushBot({
        text:
          "Hi 👋 I'm the HRIS assistant. I can help you sign in, reset a forgotten or expired password, set up OTP, or find the right contact. What do you need help with?",
      });
      this.quickReplies = this.mainMenu();
    },
    mainMenu() {
      return [
        { id: "forgot", label: "I forgot my password" },
        { id: "expired", label: "My password expired" },
        { id: "change", label: "How do I change my password?" },
        { id: "first", label: "First-time login" },
        { id: "username", label: "What's my username?" },
        { id: "otp", label: "OTP / verification code issue" },
        { id: "locked", label: "Account locked / 'Invalid credentials'" },
        { id: "browser", label: "Page keeps loading / blank screen" },
        { id: "contact", label: "Talk to HR / IT support" },
      ];
    },
    pushUser(text) {
      this.messages.push({ from: "user", text });
      this.$nextTick(this.scrollToBottom);
    },
    pushBot(payload, opts = {}) {
      const delay = opts.delay != null ? opts.delay : 450;
      this.typing = true;
      this.quickReplies = [];
      this.$nextTick(this.scrollToBottom);
      setTimeout(() => {
        this.typing = false;
        this.messages.push({ from: "bot", ...payload });
        if (opts.quickReplies) this.quickReplies = opts.quickReplies;
        this.$nextTick(this.scrollToBottom);
      }, delay);
    },
    scrollToBottom() {
      const el = this.$refs.scrollEl;
      if (el) el.scrollTop = el.scrollHeight + 200;
    },
    sendUserMessage() {
      const text = this.userInput.trim();
      if (!text) return;
      this.userInput = "";
      this.pushUser(text);
      this.routeText(text);
    },
    handleQuickReply(reply) {
      this.pushUser(reply.label);
      this.handleIntent(reply.id);
    },
    routeText(text) {
      const t = text.toLowerCase();

      if (this.awaitingIdentifierFor === "reset") {
        return this.handleIdentifierForReset(text);
      }

      if (/(forgot|forgotten|lost).*(password|pwd)/.test(t)) return this.handleIntent("forgot");
      if (/(expire|expired|expir(y|ation))/.test(t)) return this.handleIntent("expired");
      if (/(change|update|new).*(password|pwd)/.test(t)) return this.handleIntent("change");
      if (/(first[- ]?time|new (employee|user)|never logged)/.test(t)) return this.handleIntent("first");
      if (/(username|user name|login id|user id|what.*username)/.test(t)) return this.handleIntent("username");
      if (/(otp|one[- ]?time|verification code|2fa|two.?factor)/.test(t)) return this.handleIntent("otp");
      if (/(lock|locked|invalid|wrong|incorrect|too many)/.test(t)) return this.handleIntent("locked");
      if (/(blank|white screen|stuck|loading|spinning|slow|504|timeout|cant load|can't load|cannot load)/.test(t)) {
        return this.handleIntent("browser");
      }
      if (/(contact|help desk|helpdesk|support|hr|it )/.test(t)) return this.handleIntent("contact");
      if (/(hi|hello|hey|good (morning|afternoon|evening))/.test(t)) {
        return this.pushBot(
          { text: "Hello! Pick one of the options below and I'll guide you step-by-step." },
          { quickReplies: this.mainMenu() }
        );
      }

      this.pushBot(
        {
          text:
            "I didn't quite catch that. I can help with passwords, OTP, locked accounts, or contacting support. Try one of these:",
        },
        { quickReplies: this.mainMenu() }
      );
    },
    handleIntent(id) {
      switch (id) {
        case "forgot":
          return this.intentForgot();
        case "expired":
          return this.intentExpired();
        case "change":
          return this.intentChange();
        case "first":
          return this.intentFirstLogin();
        case "username":
          return this.intentUsername();
        case "otp":
          return this.intentOtp();
        case "locked":
          return this.intentLocked();
        case "browser":
          return this.intentBrowser();
        case "contact":
          return this.intentContact();
        case "menu":
          return this.pushBot(
            { text: "What else can I help you with?" },
            { quickReplies: this.mainMenu() }
          );
        case "resend":
          this.awaitingIdentifierFor = "reset";
          return this.pushBot({
            text: "Sure — type the email or national ID number you registered with.",
          });
        case "open-forgot":
          this.$router.push("/forgot-password");
          return;
      }
    },
    intentForgot() {
      this.pushBot(
        {
          html:
            "<strong>Resetting a forgotten password</strong><br/>The system will email a temporary password to the address on your HR record. Then you'll change it after signing in.",
          steps: [
            "Click <strong>Forgot password?</strong> below the password field, or use the button below.",
            "Enter the <strong>email or national ID</strong> on file with HR.",
            "Open your inbox (and <strong>spam folder</strong>) for the temporary password.",
            "Sign in with the temporary password — you'll be prompted to set a new one.",
          ],
          actions: [
            { id: "send-reset", label: "Send me a reset email now", icon: "bx bx-mail-send", variant: "primary" },
            { id: "open-forgot", label: "Open Forgot Password page", icon: "bx bx-link-external" },
          ],
        },
        { quickReplies: [{ id: "menu", label: "Back to menu" }] }
      );
    },
    intentExpired() {
      this.pushBot(
        {
          html:
            "<strong>Expired password</strong><br/>For security, passwords expire periodically. The fix is the same as a forgotten password — request a temporary one and set a new password after signing in.",
          steps: [
            "Click <strong>Forgot password?</strong> on this page.",
            "Enter your email or national ID.",
            "Sign in with the temporary password emailed to you.",
            "You'll be redirected to <strong>Change Password</strong> automatically — choose a new one that you haven't used before.",
          ],
          actions: [
            { id: "send-reset", label: "Send me a new temporary password", icon: "bx bx-mail-send", variant: "primary" },
            { id: "open-forgot", label: "Open Forgot Password page", icon: "bx bx-link-external" },
          ],
        },
        { quickReplies: [{ id: "change", label: "Password rules" }, { id: "menu", label: "Back to menu" }] }
      );
    },
    intentChange() {
      this.pushBot(
        {
          html:
            "<strong>Changing your password (when logged in)</strong>",
          steps: [
            "After signing in, open your <strong>profile menu</strong> in the top-right corner.",
            "Choose <strong>Change Password</strong>.",
            "Enter your current password, then the new password twice.",
            "<strong>Password rules:</strong> at least 8 characters, mix upper and lower case, at least 1 number and 1 symbol, and not the same as your last few passwords.",
          ],
        },
        { quickReplies: [{ id: "forgot", label: "I forgot the current one" }, { id: "menu", label: "Back to menu" }] }
      );
    },
    intentFirstLogin() {
      this.pushBot(
        {
          html: "<strong>First-time login</strong>",
          steps: [
            "Your username is your <strong>national ID</strong> (the same one used when you were registered by HR).",
            "Use the temporary password HR or the system sent you by email/SMS.",
            "On first sign-in you'll be asked to set a new password and (optionally) verify your phone for OTP.",
            "If you didn't receive a temporary password, use <strong>Forgot password?</strong> to send one to your email.",
          ],
          actions: [
            { id: "send-reset", label: "Send me a temporary password", icon: "bx bx-mail-send", variant: "primary" },
          ],
        },
        { quickReplies: [{ id: "username", label: "I don't know my username" }, { id: "menu", label: "Back to menu" }] }
      );
    },
    intentUsername() {
      this.pushBot(
        {
          html:
            "Your <strong>username</strong> is the <strong>national ID number</strong> on your HR record. If your record uses a staff number instead, try that.",
          steps: [
            "Type your national ID in the <strong>Username</strong> field.",
            "If it's rejected, contact HR to confirm what's on file.",
          ],
        },
        { quickReplies: [{ id: "contact", label: "Contact HR" }, { id: "menu", label: "Back to menu" }] }
      );
    },
    intentOtp() {
      this.pushBot(
        {
          html:
            "<strong>OTP / verification code</strong><br/>An OTP may be sent to your phone or email after sign-in.",
          steps: [
            "Codes are valid for a few minutes — request a new one if it expires.",
            "Check both <strong>SMS</strong> and <strong>email</strong>; SMS can be delayed.",
            "Make sure your phone number on file is correct (HR can update it).",
            "If you never receive a code, contact IT support to disable OTP temporarily on your account.",
          ],
        },
        { quickReplies: [{ id: "contact", label: "Contact IT support" }, { id: "menu", label: "Back to menu" }] }
      );
    },
    intentLocked() {
      this.pushBot(
        {
          html:
            "<strong>Locked or 'Invalid credentials'</strong>",
          steps: [
            "Make sure <strong>Caps Lock</strong> is off and there are no trailing spaces.",
            "If you've tried several times, wait <strong>10–15 minutes</strong> and try again.",
            "Still no luck? Reset your password — that also clears most lockouts.",
            "If the message says <em>account suspended</em> or <em>disabled</em>, contact HR — only they can re-enable it.",
          ],
          actions: [
            { id: "send-reset", label: "Reset my password", icon: "bx bx-refresh", variant: "primary" },
          ],
        },
        { quickReplies: [{ id: "contact", label: "Contact support" }, { id: "menu", label: "Back to menu" }] }
      );
    },
    intentBrowser() {
      this.pushBot(
        {
          html: "<strong>Page won't load or feels stuck?</strong>",
          steps: [
            "Refresh the page (Ctrl + F5 / Cmd + Shift + R).",
            "Try a different browser — <strong>Chrome</strong> or <strong>Edge</strong> work best.",
            "Clear your browser cache and cookies for this site.",
            "If you see a <em>504</em> or timeout, the server may be busy — wait 1–2 minutes and try again.",
          ],
        },
        { quickReplies: [{ id: "contact", label: "Still not working" }, { id: "menu", label: "Back to menu" }] }
      );
    },
    intentContact() {
      this.pushBot(
        {
          html:
            "<strong>Need a human?</strong> Reach out using any of these:",
          steps: [
            "<i class='bx bx-envelope'></i> HR Desk: <a href='mailto:hr@homabay.go.ke'>hr@homabay.go.ke</a>",
            "<i class='bx bx-support'></i> IT Support: <a href='mailto:ict@homabay.go.ke'>ict@homabay.go.ke</a>",
            "<i class='bx bx-phone'></i> Help line during office hours (Mon–Fri 8am–5pm).",
            "Visit the HR office at the County Headquarters, ground floor.",
          ],
        },
        { quickReplies: [{ id: "menu", label: "Back to menu" }] }
      );
    },
    handleIdentifierForReset(text) {
      const id = text.trim();
      if (!EMAIL_RE.test(id) && !ID_RE.test(id)) {
        return this.pushBot({
          text:
            "Hmm, that doesn't look like a valid email or national ID (6–12 digits). Please try again — or click 'Open Forgot Password page' to use the full form.",
        });
      }
      this.lastIdentifier = id;
      this.awaitingIdentifierFor = null;
      this.sendResetEmail(id);
    },
    async runAction(act) {
      switch (act.id) {
        case "send-reset":
          if (this.lastIdentifier) {
            return this.sendResetEmail(this.lastIdentifier);
          }
          this.awaitingIdentifierFor = "reset";
          return this.pushBot({
            text:
              "Sure. What's the <strong>email address</strong> or <strong>national ID</strong> registered with HR? I'll send a temporary password to your email and registered phone (SMS).",
            html:
              "Sure. What's the <strong>email address</strong> or <strong>national ID</strong> registered with HR? I'll send a temporary password to your email and registered phone (SMS).",
          });
        case "open-forgot":
          this.toggle();
          this.$router.push("/forgot-password");
          return;
        case "menu":
          return this.handleIntent("menu");
        default:
          return this.handleIntent(act.id);
      }
    },
    async sendResetEmail(identifier) {
      if (this.sendingReset) return;
      this.sendingReset = true;
      this.pushBot({ text: `Sending a temporary password for ${identifier}…` }, { delay: 200 });

      try {
        const fd = new FormData();
        fd.append("function", "forgotPassword");
        fd.append("identifier", identifier);
        const res = await execute2(fd, authUrl);
        if (res?.data?.success) {
          const payload = res?.data?.data || {};
          const via = [];
          if (payload.emailSent) via.push("email");
          if (payload.smsSent) via.push("SMS");
          const viaText = via.length ? via.join(" and ") : "your registered contact details";
          this.pushBot(
            {
              html:
                `✅ <strong>Done.</strong> A temporary password has been sent via ${viaText}. Check your inbox, spam folder, and phone.`,
              steps: [
                "Sign in here with that temporary password.",
                "You'll be redirected to set a new password.",
              ],
            },
            {
              quickReplies: [
                { id: "resend", label: "Send to a different address" },
                { id: "menu", label: "Back to menu" },
              ],
            }
          );
        } else {
          this.pushBot(
            {
              text:
                (res?.data?.message || "I couldn't send the temporary password.") +
                " Make sure the email or ID is on file with HR, or contact support.",
            },
            {
              quickReplies: [
                { id: "resend", label: "Try a different address" },
                { id: "contact", label: "Contact support" },
              ],
            }
          );
        }
      } catch (e) {
        this.pushBot(
          {
            text:
              "Sorry, I couldn't reach the server. Check your connection and try again, or contact IT support.",
          },
          { quickReplies: [{ id: "contact", label: "Contact support" }] }
        );
      } finally {
        this.sendingReset = false;
      }
    },
  },
};
</script>

<style scoped>
.hb-assist-root {
  position: fixed;
  right: 22px;
  bottom: 22px;
  z-index: 9999;
  font-family: -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, "Helvetica Neue", sans-serif;
}

.hb-assist-launcher {
  border: none;
  background: linear-gradient(135deg, #2195d3, #0e3a1b);
  color: #ffffff;
  border-radius: 999px;
  padding: 12px 18px;
  display: inline-flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  box-shadow: 0 12px 32px rgba(33, 149, 211, 0.35), 0 4px 12px rgba(14, 58, 27, 0.25);
  font-weight: 600;
  font-size: 14px;
  transition: transform 0.18s ease, box-shadow 0.18s ease;
}
.hb-assist-launcher i {
  font-size: 22px;
}
.hb-assist-launcher:hover {
  transform: translateY(-2px);
}
.hb-assist-root.open .hb-assist-launcher {
  padding: 12px;
  border-radius: 50%;
}
.hb-assist-launcher.pulse {
  animation: hb-assist-pulse 2.2s ease-in-out infinite;
}
@keyframes hb-assist-pulse {
  0%, 100% { box-shadow: 0 12px 32px rgba(33, 149, 211, 0.35), 0 0 0 0 rgba(33, 149, 211, 0.45); }
  50% { box-shadow: 0 12px 32px rgba(33, 149, 211, 0.35), 0 0 0 14px rgba(33, 149, 211, 0); }
}

.hb-assist-panel {
  position: absolute;
  bottom: 64px;
  right: 0;
  width: 380px;
  max-width: calc(100vw - 32px);
  height: 540px;
  max-height: calc(100vh - 100px);
  background: #ffffff;
  border-radius: 18px;
  box-shadow: 0 24px 60px rgba(15, 23, 42, 0.25), 0 6px 16px rgba(15, 23, 42, 0.12);
  display: flex;
  flex-direction: column;
  overflow: hidden;
  border: 1px solid #e5e7eb;
}

.hb-assist-header {
  display: flex;
  align-items: center;
  gap: 12px;
  padding: 14px 16px;
  background: linear-gradient(135deg, #0e3a1b, #2195d3);
  color: #ffffff;
}
.hb-assist-avatar {
  width: 38px;
  height: 38px;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.18);
  display: flex;
  align-items: center;
  justify-content: center;
  font-size: 22px;
}
.hb-assist-title-wrap { flex: 1; min-width: 0; }
.hb-assist-title-wrap h4 {
  margin: 0;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.1;
}
.hb-assist-title-wrap p {
  margin: 2px 0 0;
  font-size: 11px;
  opacity: 0.9;
  display: flex;
  align-items: center;
  gap: 6px;
}
.hb-assist-status-dot {
  width: 8px;
  height: 8px;
  border-radius: 50%;
  background: #4ade80;
  box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.6);
  animation: hb-assist-online 1.8s ease-in-out infinite;
}
@keyframes hb-assist-online {
  0%, 100% { box-shadow: 0 0 0 0 rgba(74, 222, 128, 0.6); }
  50% { box-shadow: 0 0 0 6px rgba(74, 222, 128, 0); }
}
.hb-assist-icon-btn {
  border: none;
  background: rgba(255, 255, 255, 0.12);
  color: #fff;
  width: 32px;
  height: 32px;
  border-radius: 8px;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: background 0.15s ease;
}
.hb-assist-icon-btn:hover {
  background: rgba(255, 255, 255, 0.22);
}

.hb-assist-body {
  flex: 1;
  overflow-y: auto;
  padding: 14px 14px 6px;
  background: #f8fafc;
}

.hb-assist-msg {
  display: flex;
  margin-bottom: 10px;
}
.hb-assist-msg.bot { justify-content: flex-start; }
.hb-assist-msg.user { justify-content: flex-end; }

.hb-assist-bubble {
  max-width: 86%;
  padding: 10px 12px;
  border-radius: 14px;
  font-size: 13.5px;
  line-height: 1.45;
  color: #0f172a;
  background: #ffffff;
  border: 1px solid #e5e7eb;
  border-bottom-left-radius: 4px;
  box-shadow: 0 1px 2px rgba(15, 23, 42, 0.04);
}
.hb-assist-bubble.user-bubble {
  background: linear-gradient(135deg, #2195d3, #1f7fbf);
  color: #ffffff;
  border: none;
  border-bottom-left-radius: 14px;
  border-bottom-right-radius: 4px;
}
.hb-assist-bubble :deep(a) {
  color: #2195d3;
  text-decoration: underline;
}

.hb-assist-bubble.typing {
  display: inline-flex;
  gap: 4px;
  align-items: center;
  padding: 12px 14px;
}
.hb-assist-bubble.typing .dot {
  width: 6px;
  height: 6px;
  border-radius: 50%;
  background: #94a3b8;
  animation: hb-assist-typing 1.2s infinite ease-in-out;
}
.hb-assist-bubble.typing .dot:nth-child(2) { animation-delay: 0.15s; }
.hb-assist-bubble.typing .dot:nth-child(3) { animation-delay: 0.3s; }
@keyframes hb-assist-typing {
  0%, 80%, 100% { transform: translateY(0); opacity: 0.4; }
  40% { transform: translateY(-4px); opacity: 1; }
}

.hb-assist-steps {
  margin: 10px 0 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: 8px;
}
.hb-assist-steps li {
  display: flex;
  gap: 10px;
  align-items: flex-start;
  font-size: 13px;
}
.hb-assist-step-num {
  flex-shrink: 0;
  width: 22px;
  height: 22px;
  border-radius: 50%;
  background: #e0f2fe;
  color: #0369a1;
  font-size: 12px;
  font-weight: 700;
  display: inline-flex;
  align-items: center;
  justify-content: center;
}

.hb-assist-actions {
  margin-top: 12px;
  display: flex;
  flex-wrap: wrap;
  gap: 8px;
}
.hb-assist-action {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #0f172a;
  border-radius: 999px;
  padding: 7px 14px;
  font-size: 12.5px;
  font-weight: 600;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  gap: 6px;
  transition: background 0.15s ease, transform 0.15s ease;
}
.hb-assist-action:hover { background: #f1f5f9; transform: translateY(-1px); }
.hb-assist-action.primary {
  background: linear-gradient(135deg, #2195d3, #1d4ed8);
  border-color: transparent;
  color: #ffffff;
}
.hb-assist-action:disabled { opacity: 0.6; cursor: not-allowed; transform: none; }

.hb-assist-quick {
  display: flex;
  flex-wrap: wrap;
  gap: 6px;
  padding: 10px 14px 4px;
  background: #f8fafc;
  border-top: 1px solid #e5e7eb;
}
.hb-assist-chip {
  border: 1px solid #cbd5e1;
  background: #ffffff;
  color: #0369a1;
  border-radius: 999px;
  padding: 6px 12px;
  font-size: 12px;
  cursor: pointer;
  transition: background 0.15s ease, color 0.15s ease;
}
.hb-assist-chip:hover {
  background: #2195d3;
  color: #ffffff;
  border-color: #2195d3;
}

.hb-assist-input {
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 10px 12px;
  border-top: 1px solid #e5e7eb;
  background: #ffffff;
}
.hb-assist-input input {
  flex: 1;
  border: 1px solid #e5e7eb;
  border-radius: 999px;
  padding: 9px 14px;
  font-size: 13px;
  outline: none;
  transition: border-color 0.15s ease, box-shadow 0.15s ease;
}
.hb-assist-input input:focus {
  border-color: #2195d3;
  box-shadow: 0 0 0 3px rgba(33, 149, 211, 0.18);
}
.hb-assist-input button {
  border: none;
  background: #2195d3;
  color: #ffffff;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  cursor: pointer;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  font-size: 18px;
  transition: background 0.15s ease;
}
.hb-assist-input button:disabled {
  background: #cbd5e1;
  cursor: not-allowed;
}

.hb-assist-footer {
  padding: 8px 14px 10px;
  background: #f1f5f9;
  color: #64748b;
  font-size: 11px;
  display: flex;
  align-items: center;
  gap: 6px;
  border-top: 1px solid #e5e7eb;
}

/* Open / close animation */
.hb-assist-pop-enter-active,
.hb-assist-pop-leave-active {
  transition: transform 0.22s cubic-bezier(0.34, 1.56, 0.64, 1), opacity 0.18s ease;
}
.hb-assist-pop-enter-from,
.hb-assist-pop-leave-to {
  opacity: 0;
  transform: translateY(12px) scale(0.95);
}

/* Responsive */
@media (max-width: 480px) {
  .hb-assist-root { right: 12px; bottom: 12px; }
  .hb-assist-panel {
    width: calc(100vw - 24px);
    height: 70vh;
    bottom: 60px;
  }
  .hb-assist-launcher-text { display: none; }
  .hb-assist-launcher { padding: 12px; border-radius: 50%; }
}
</style>
